/**
 * Field State
 *
 * Unifies field state detection across template-driven forms (`[(ngModel)]`) and Signal Forms
 * (`[formField]`) for a single control, replacing the retired two-class `FormFieldStateAdapter`
 * split (`NgModelFormFieldStateAdapter` / `SignalFieldStateAdapter`) with one `FieldState` shape
 * and two small factory functions.
 *
 * `InputGroupFieldComponent` still queries `contentChild(NgModel)` / `contentChild(FormField)`
 * directly (both are already guaranteed importable by any consumer using those bindings), then
 * builds a `FieldState` from whichever resolved — this keeps the refactor fully backward
 * compatible with every existing `<td-wb-input-group-field>` consumer, with zero template
 * changes required.
 *
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`, Phase D.
 */
import { Injector, Signal, computed } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { InputStatus } from '../input-status/input-status.enum';

import type { NgModel } from '@angular/forms';
import type { FormField, ValidationError } from '@angular/forms/signals';
import type { Subscription } from 'rxjs';

export type FieldStateKind = 'ngModel' | 'signalFieldState';

export interface FieldStateValidationError {
  readonly key: string;
  readonly messageKey: string;
}

export interface FieldState {
  readonly kind: FieldStateKind;
  readonly name: Signal<string | undefined>;
  readonly dirty: Signal<boolean>;
  readonly touched: Signal<boolean>;
  readonly invalid: Signal<boolean>;
  readonly hasValue: Signal<boolean>;
  readonly hasSubmittedFlag: Signal<boolean>;
  readonly isPristineUntouched: Signal<boolean>;
  validationErrors(errorKeyNamespace: string): Array<FieldStateValidationError>;
  /**
   * Subscribes to invalid-status transitions. Kept as an explicit RxJS subscription (rather than
   * `effect()`) so callers that construct this state outside a component's own change-detection
   * cycle (e.g. direct instantiation in unit tests, or `InputErrorsComponent.subscribeToStatusChange`)
   * still observe synchronous, deterministic timing — the same contract the retired
   * `FormFieldStateAdapter` provided. Timing intentionally mirrors the two retired adapters:
   * the signal-forms variant invokes `callback` immediately with the current value; the
   * ngModel variant only invokes it on the next actual `statusChanges` emission.
   */
  subscribeStatusChange(callback: (isInvalid: boolean) => void): Subscription;
}

/**
 * Builds a `FieldState` from an `NgModel`. Re-derives its signals on every `statusChanges`
 * emission, mirroring the timing of the retired `NgModelFormFieldStateAdapter`. `markAsDirty()` /
 * `markAsTouched()` do not themselves emit `statusChanges`, so — like the code this replaces —
 * dirty/touched are only guaranteed fresh at the next value-driven revalidation.
 */
export function fieldStateFromNgModel(model: NgModel, injector: Injector): FieldState {
  // `equal: () => false` is deliberate: this signal exists purely as a re-derivation "tick" for
  // the computed signals below, not as a meaningful value in its own right. The status STRING
  // frequently stays identical across a submit (e.g. 'INVALID' the whole way through mark-dirty
  // -> mark-touched -> revalidate), so Angular's default `Object.is` equality would silently
  // treat those emissions as "no change" and never invalidate `dirty`/`touched`/`hasSubmittedFlag`/
  // `isPristineUntouched` — even though the underlying control's flags genuinely changed.
  const trigger = toSignal(model.statusChanges, { initialValue: model.status, injector, equal: (): boolean => false });

  return {
    kind: 'ngModel',
    name: computed((): string | undefined => { trigger(); return model.name; }),
    dirty: computed((): boolean => { trigger(); return model.dirty; }),
    touched: computed((): boolean => { trigger(); return model.touched; }),
    invalid: computed((): boolean => { trigger(); return model.invalid; }),
    hasValue: computed((): boolean => { trigger(); return (model.value ?? null) !== null; }),
    hasSubmittedFlag: computed((): boolean => {
      trigger();
      return Object.prototype.hasOwnProperty.call(model.control ?? {}, 'submitted');
    }),
    isPristineUntouched: computed((): boolean => {
      trigger();
      return (model.control?.pristine ?? true) && (model.control?.untouched ?? true);
    }),
    validationErrors(errorKeyNamespace: string): Array<FieldStateValidationError> {
      trigger();
      const validationErrors = model.errors ?? {};
      return Object.keys(validationErrors).map((key: string): FieldStateValidationError => ({
        key,
        messageKey: `${errorKeyNamespace}.${key}`
      }));
    },
    subscribeStatusChange(callback: (isInvalid: boolean) => void): Subscription {
      return model.statusChanges.subscribe((status: string): void => {
        callback(status === InputStatus.INVALID);
      });
    }
  };
}

/** Builds a `FieldState` from a `FormField`. Fully signal-driven — no bridging needed. */
export function fieldStateFromSignalField(formField: FormField<unknown>): FieldState {
  return {
    kind: 'signalFieldState',
    name: computed((): string => formField.state().name()),
    dirty: computed((): boolean => formField.state().dirty()),
    touched: computed((): boolean => formField.state().touched()),
    invalid: computed((): boolean => formField.state().invalid()),
    hasValue: computed((): boolean => (formField.state().value() ?? null) !== null),
    hasSubmittedFlag: computed((): boolean => false),
    isPristineUntouched: computed((): boolean => !formField.state().dirty() && !formField.state().touched()),
    validationErrors(errorKeyNamespace: string): Array<FieldStateValidationError> {
      return formField.state().errors().map((error: ValidationError.WithFieldTree): FieldStateValidationError => ({
        key: error.kind,
        messageKey: error.message ?? `${errorKeyNamespace}.${error.kind}`
      }));
    },
    subscribeStatusChange(callback: (isInvalid: boolean) => void): Subscription {
      callback(formField.state().invalid());
      // Tracks error *kinds*, not just the boolean invalid flag: a field can stay invalid while
      // which validator is failing changes (e.g. a cross-field mismatch resolving into a plain
      // pattern failure once the value itself becomes malformed). Tracking invalid() alone means
      // this never re-fires for that transition - true to true is not a change - leaving
      // InputErrorsComponent displaying a stale error kind even though errors() changed underneath
      // it. Found via `email-signal-form-flag.e2e.ts` (S1 plan §4.1 follow-up).
      const errorKindsFingerprint = computed((): string =>
        formField.state().errors().map((error: ValidationError.WithFieldTree): string => error.kind).join(',')
      );
      return toObservable(errorKindsFingerprint, { injector: formField.injector })
        .subscribe((): void => callback(formField.state().invalid()));
    }
  };
}
